/**
 * WorkspaceCopilot — cross-source grounded RAG UI.
 *
 * Searches across the user's File Vault, Content Stream (+ Signal matches),
 * and Connections graph. Reuses `CopilotCitationCard` for consistent citation
 * UX with expand-on-click "See exact quote" transparency.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles, Loader2, MessageSquare, ShieldCheck, AlertTriangle,
  FileText, Newspaper, Network as NetworkIcon, RefreshCw, Filter,
  LineChart, Bot, Layers,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '@/lib/api';
import { handleSpendError } from '@/lib/spendErrors';
import CopilotCitationCard from './vault/CopilotCitationCard';

const EXAMPLES = [
  'Which partnerships or deals recur across my sources?',
  'What risks are showing up in my Connections graph and articles?',
  'Summarize the latest news about companies in my File Vault.',
  'Who are the most-mentioned people across all my data?',
  'What signals matched documents I uploaded?',
];

// iter114.4 — Sources extended to include Board Briefing orchestrations.
const SOURCE_META = {
  file: { icon: FileText, label: 'Files', color: 'rose' },
  content: { icon: Newspaper, label: 'Articles', color: 'amber' },
  entity: { icon: NetworkIcon, label: 'Connections', color: 'violet' },
  economist: { icon: LineChart, label: 'Economist', color: 'blue' },
  agent: { icon: Bot, label: 'Agents', color: 'emerald' },
  orchestration: { icon: Layers, label: 'Orchestrations', color: 'indigo' },
};
const ALL_SOURCE_KEYS = Object.keys(SOURCE_META);

// iter114.4 — Full color lookup for the on/active chip state. Previously the
// on-state used a rose/amber/violet ternary that silently collapsed blue,
// emerald, and indigo to violet. This map keeps every accent honest.
const CHIP_ON_CLASSES = {
  rose:     'bg-rose-950/60 border-rose-800/50 text-rose-200',
  amber:    'bg-amber-950/60 border-amber-800/50 text-amber-200',
  violet:   'bg-violet-950/60 border-violet-800/50 text-violet-200',
  blue:     'bg-blue-950/60 border-blue-800/50 text-blue-200',
  emerald:  'bg-emerald-950/60 border-emerald-800/50 text-emerald-200',
  indigo:   'bg-indigo-950/60 border-indigo-800/50 text-indigo-200',
};
const CHIP_OFF = 'bg-[#0A1024] border-[#1F2A4D] text-slate-500';

const WorkspaceCopilot = ({ compact = false, initialQuestion = '' }) => {
  const navigate = useNavigate();
  const [question, setQuestion] = useState(initialQuestion || '');
  const [selectedSources, setSelectedSources] = useState(ALL_SOURCE_KEYS);
  const [loading, setLoading] = useState(false);
  const [reindexing, setReindexing] = useState(false);
  const [answer, setAnswer] = useState(null);
  const [status, setStatus] = useState(null);

  const loadStatus = useCallback(async () => {
    try {
      const r = await api.get('/copilot/workspace/status');
      setStatus(r.data);
    } catch {
      /* non-fatal — the widget just shows without status */
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // iter114.3 — Sync incoming `initialQuestion` (e.g. from ?q=<encoded>) into
  // the textarea whenever the deep-link value changes. Empty updates never
  // clobber a user-typed question because we gate on truthy.
  useEffect(() => {
    if (initialQuestion) setQuestion(initialQuestion);
  }, [initialQuestion]);

  const ask = async (q) => {
    const query = (q ?? question).trim();
    if (!query) return toast.error('Ask a question about your workspace.');
    setLoading(true);
    setAnswer(null);
    try {
      const r = await api.post('/copilot/workspace', {
        question: query,
        sources: selectedSources.length === ALL_SOURCE_KEYS.length ? undefined : selectedSources,
      });
      setAnswer(r.data);
    } catch (e) {
      handleSpendError(e, navigate, 'Workspace Copilot failed');
    } finally {
      setLoading(false);
    }
  };

  const runReindex = async () => {
    setReindexing(true);
    try {
      const r = await api.post('/copilot/reindex-workspace');
      const totalFiles = r.data?.files?.chunks || 0;
      const totalContent = r.data?.content?.chunks || 0;
      const totalEntities = r.data?.entities?.chunks || 0;
      const totalEconomist = r.data?.economist?.chunks || 0;
      const totalAgents = r.data?.agents?.chunks || 0;
      const totalOrchestrations = r.data?.orchestrations?.chunks || 0;
      toast.success(
        `Workspace indexed: ${totalFiles} file · ${totalContent} article · ${totalEntities} entity · ${totalEconomist} economist · ${totalAgents} agent · ${totalOrchestrations} orchestration chunks.`,
      );
      loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reindex failed');
    } finally {
      setReindexing(false);
    }
  };

  const toggleSource = (k) => {
    setSelectedSources((prev) => {
      if (prev.includes(k)) {
        // Prevent removing the last source
        return prev.length > 1 ? prev.filter((x) => x !== k) : prev;
      }
      return [...prev, k];
    });
  };

  const openSource = (cit) => {
    if (!cit.source_id) return;
    const id = encodeURIComponent(cit.source_id);
    switch (cit.source_kind) {
      case 'file':          navigate(`/dashboard/vault?open=${id}`); break;
      case 'content':       navigate(`/dashboard/?content=${id}`); break;
      case 'entity':        navigate(`/dashboard/connections?entity=${id}`); break;
      case 'economist':     navigate(`/dashboard/economist?tab=saved&run=${id}`); break;
      case 'agent':         navigate(`/dashboard/ai-agents/history?run=${id}`); break;
      case 'orchestration': navigate(`/dashboard/ai-agents/history?orch=${id}`); break;
      default: break;
    }
  };

  const renderAnswerBody = (text, citations) => {
    if (!text) return null;
    const parts = String(text).split(/(\[\d+\])/g);
    return parts.map((p, i) => {
      const key = `${i}:${p}`;
      const m = p.match(/^\[(\d+)\]$/);
      if (!m) return <span key={key}>{p}</span>;
      const n = parseInt(m[1], 10);
      const cite = (citations || []).find((c) => c.index === n) || citations?.[n - 1];
      if (!cite) return <span key={key} className="text-slate-400">{p}</span>;
      return (
        <button
          key={key}
          type="button"
          onClick={() => openSource(cite)}
          className="align-baseline mx-0.5 inline-flex items-center rounded bg-rose-950/60 border border-rose-800/50 px-1 py-0 text-[10px] leading-4 text-rose-200 hover:bg-rose-900/60 transition"
          title={cite.source_name}
          data-testid={`workspace-copilot-marker-${n}`}
        >
          [{n}]
        </button>
      );
    });
  };

  const totalChunks = status?.total_chunks || 0;
  const hasIndex = totalChunks > 0;

  return (
    <Card data-testid="workspace-copilot">
      <CardContent className={compact ? 'p-3 space-y-2' : 'p-4 space-y-3'}>
        <div className="flex items-start gap-2">
          <div className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-violet-950/60 border border-violet-800/40 shrink-0">
            <Sparkles className="h-4 w-4 text-violet-300" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-slate-100 flex items-center gap-2 flex-wrap">
              Ask your workspace
              {status && (
                <Badge className="text-[10px] bg-[#131C3A] text-slate-400 border-slate-700">
                  {totalChunks} chunks indexed
                </Badge>
              )}
            </div>
            <div className="text-[11px] text-slate-500">
              Grounded, cited answers across your Files, Content Stream, and
              Connections graph. 1 AI credit per query.
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={runReindex}
            disabled={reindexing}
            className="border-[#1F2A4D] bg-[#0A1024] hover:bg-[#131C3A] text-slate-200 h-8"
            data-testid="workspace-copilot-reindex"
            title="Re-embed files, articles, and entities"
          >
            {reindexing
              ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Indexing…</>
              : <><RefreshCw className="h-3.5 w-3.5 mr-1" /> Reindex</>}
          </Button>
        </div>

        <Textarea
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          rows={compact ? 2 : 3}
          placeholder="e.g. What common risks or opportunities emerge across my Files, Signals, and Connections?"
          className="bg-[#0A1024] border-[#1F2A4D] text-slate-100 text-xs"
          data-testid="workspace-copilot-input"
        />

        {/* Source filters */}
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1">
            <Filter className="h-2.5 w-2.5" /> Sources:
          </span>
          {Object.entries(SOURCE_META).map(([k, m]) => {
            const on = selectedSources.includes(k);
            const Icon = m.icon;
            const count = status?.counts?.[k] || 0;
            const onClass = CHIP_ON_CLASSES[m.color] || CHIP_ON_CLASSES.violet;
            return (
              <button
                key={k}
                type="button"
                onClick={() => toggleSource(k)}
                className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] transition ${on ? onClass : CHIP_OFF}`}
                data-testid={`workspace-source-toggle-${k}`}
                title={on ? `Click to exclude ${m.label}` : `Click to include ${m.label}`}
              >
                <Icon className="h-3 w-3" />
                {m.label}
                {count > 0 && <span className="opacity-70">({count})</span>}
              </button>
            );
          })}
        </div>

        {!compact && (
          <div className="flex flex-wrap gap-1.5 items-center">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mr-1">Try:</span>
            {EXAMPLES.map((q) => (
              <button
                key={q}
                type="button"
                onClick={() => { setQuestion(q); ask(q); }}
                disabled={loading}
                className="text-[10px] px-2 py-0.5 rounded border border-[#1F2A4D] bg-[#0A1024] text-slate-300 hover:border-violet-800/50 hover:text-violet-200 transition"
              >
                {q}
              </button>
            ))}
          </div>
        )}

        <div className="flex justify-end">
          <Button
            onClick={() => ask()}
            disabled={loading}
            className="bg-violet-600 hover:bg-violet-700 text-white"
            data-testid="workspace-copilot-ask"
          >
            {loading
              ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              : <MessageSquare className="h-4 w-4 mr-1.5" />}
            Ask workspace
          </Button>
        </div>

        {answer && (
          <div className="rounded-md bg-[#0A1024] border border-[#1F2A4D] p-3 space-y-2" data-testid="workspace-copilot-answer">
            {answer.insufficient_evidence ? (
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 text-amber-400 shrink-0" />
                <div className="flex-1">
                  <div className="text-sm text-amber-200 font-semibold">Insufficient evidence</div>
                  <p className="text-xs text-amber-100/90 mt-1 whitespace-pre-wrap">{answer.answer}</p>
                  {(answer.needs_reindex || !hasIndex) && (
                    <div className="mt-3">
                      <Button
                        onClick={runReindex}
                        disabled={reindexing}
                        size="sm"
                        className="bg-violet-600 hover:bg-violet-700 text-white"
                        data-testid="workspace-copilot-reindex-cta"
                      >
                        <RefreshCw className={`h-3 w-3 mr-1.5 ${reindexing ? 'animate-spin' : ''}`} />
                        Index my workspace now
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2">
                <ShieldCheck className="h-4 w-4 mt-0.5 text-emerald-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-emerald-300 font-semibold">Answer</div>
                  <div className="text-sm text-slate-100 mt-0.5 whitespace-pre-wrap leading-relaxed">
                    {renderAnswerBody(answer.answer, answer.citations)}
                  </div>
                  <div className="mt-1 flex items-center gap-2 flex-wrap">
                    <Badge className="text-[10px] bg-emerald-950/60 text-emerald-200 border-emerald-800/40">
                      Confidence: {Math.round((answer.confidence || 0) * 100)}%
                    </Badge>
                    {Object.entries(answer.used_sources || {}).map(([k, n]) => {
                      if (!n) return null;
                      const m = SOURCE_META[k];
                      if (!m) return null;
                      return (
                        <Badge key={k} className="text-[10px] bg-[#131C3A] text-slate-300 border-slate-700">
                          {n} {m.label.toLowerCase()}
                        </Badge>
                      );
                    })}
                    {answer.credits_charged > 0 && (
                      <Badge className="text-[10px] bg-violet-950/60 text-violet-200 border-violet-800/40">
                        {answer.credits_charged} credit
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            )}

            {(answer.citations || []).length > 0 && !answer.insufficient_evidence && (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1 mt-2">
                  Sources
                </div>
                <div className="space-y-1">
                  {answer.citations.map((c) => (
                    <CopilotCitationCard
                      key={`${c.source_kind}:${c.source_id}`}
                      citation={c}
                      onOpen={openSource}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default WorkspaceCopilot;
